import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { ArrowLeft, Save, User, Heart, Phone, FileText, Users } from 'lucide-react';

interface Resident {
  id: string;
  full_name: string;
  room_number: string | null;
  join_code: string;
  status: string;
  created_at: string;
  email: string | null;
  date_of_birth: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  emergency_contact_relation: string | null;
  medical_notes: string | null;
  allergies: string | null;
  care_preferences: string | null;
  mobility_level: string | null;
  diet_requirements: string | null;
  physician_name: string | null;
  physician_phone: string | null;
  preferred_language: string | null;
}

interface CareNote {
  id: string;
  author_name: string;
  note: string;
  shift: string;
  created_at: string;
  note_type?: string;
}

const MOBILITY_OPTIONS = ['Independent', 'Supervision Required', 'Limited Assistance', 'Full Assistance', 'Non-ambulatory'];
const LANGUAGE_OPTIONS = ['English', 'French', 'Spanish', 'Mandarin', 'Cantonese', 'Punjabi', 'Tagalog', 'Other'];

export function ResidentProfilePage() {
  const { residentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [resident, setResident] = useState<Resident | null>(null);
  const [notes, setNotes] = useState<CareNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'medical' | 'emergency' | 'care' | 'notes'>('overview');
  const [form, setForm] = useState<Partial<Resident>>({});

  useEffect(() => {
    if (user && residentId) loadProfile();
  }, [user, residentId]);

  async function loadProfile() {
    if (!supabase || !user) return;
    setLoading(true);
    try {
      const { data: resident, error } = await supabase
        .from('residents').select('*').eq('id', residentId).single();
      if (error) throw error;
      setResident(resident);
      setForm(resident);

      const { data: noteData } = await supabase
        .from('care_notes')
        .select('id, author_name, note, shift, created_at, note_type')
        .eq('resident_id', residentId)
        .order('created_at', { ascending: false })
        .limit(20);
      setNotes(noteData || []);
    } catch (e) {
      console.error('Failed to load resident:', e);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!supabase || !residentId) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('residents').update({
        date_of_birth: form.date_of_birth || null,
        emergency_contact_name: form.emergency_contact_name || null,
        emergency_contact_phone: form.emergency_contact_phone || null,
        emergency_contact_relation: form.emergency_contact_relation || null,
        medical_notes: form.medical_notes || null,
        allergies: form.allergies || null,
        care_preferences: form.care_preferences || null,
        mobility_level: form.mobility_level || null,
        diet_requirements: form.diet_requirements || null,
        physician_name: form.physician_name || null,
        physician_phone: form.physician_phone || null,
        preferred_language: form.preferred_language || 'English',
      }).eq('id', residentId);
      if (error) throw error;
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  }

  function field(label: string, key: keyof Resident, type: 'text' | 'date' | 'textarea' = 'text') {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">{label}</label>
        {type === 'textarea' ? (
          <textarea
            rows={3}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            value={(form[key] as string) || ''}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          />
        ) : (
          <input
            type={type}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            value={(form[key] as string) || ''}
            onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          />
        )}
      </div>
    );
  }

  function selectField(label: string, key: keyof Resident, options: string[]) {
    return (
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">{label}</label>
        <select
          className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          value={(form[key] as string) || ''}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        >
          <option value="">Select...</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }

  if (loading) return (
    <div className="admin-root flex items-center justify-center h-64">
      <div className="text-[var(--color-text-muted)] text-sm">Loading resident profile...</div>
    </div>
  );

  if (!resident) return (
    <div className="admin-root flex items-center justify-center h-64">
      <div className="text-[var(--color-text-muted)] text-sm">Resident not found.</div>
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'medical', label: 'Medical', icon: Heart },
    { id: 'emergency', label: 'Emergency', icon: Phone },
    { id: 'care', label: 'Care Plan', icon: FileText },
    { id: 'notes', label: 'Care Notes', icon: Users },
  ] as const;

  return (
    <div className="admin-root p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/admin/residents')}
          className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Residents
        </button>
      </div>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text)]">{resident.full_name}</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Room {resident.room_number || '—'} · Joined {new Date(resident.created_at).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' })} · Join Code: <span className="font-mono">{resident.join_code}</span>
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Save size={15} />
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[var(--color-surface)] rounded-2xl p-1 border border-[var(--color-border)]">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[var(--color-primary)] text-white shadow-sm'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="bg-[var(--color-surface)] rounded-2xl border border-[var(--color-border)] p-6">

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('Full Name', 'full_name')}
            {field('Room Number', 'room_number')}
            {field('Date of Birth', 'date_of_birth', 'date')}
            {selectField('Preferred Language', 'preferred_language', LANGUAGE_OPTIONS)}
            <div className="sm:col-span-2">
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-[var(--color-text-muted)]">Status:</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${resident.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                  {resident.status}
                </span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'medical' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('Physician Name', 'physician_name')}
            {field('Physician Phone', 'physician_phone')}
            {selectField('Mobility Level', 'mobility_level', MOBILITY_OPTIONS)}
            {field('Diet Requirements', 'diet_requirements')}
            {field('Allergies', 'allergies', 'textarea')}
            {field('Medical Notes', 'medical_notes', 'textarea')}
          </div>
        )}

        {activeTab === 'emergency' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('Emergency Contact Name', 'emergency_contact_name')}
            {field('Relationship', 'emergency_contact_relation')}
            {field('Phone Number', 'emergency_contact_phone')}
          </div>
        )}

        {activeTab === 'care' && (
          <div className="grid grid-cols-1 gap-4">
            {field('Care Preferences', 'care_preferences', 'textarea')}
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="flex flex-col gap-3">
            {notes.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)] text-center py-8">No care notes recorded yet.</p>
            ) : notes.map(note => (
              <div key={note.id} className="border border-[var(--color-border)] rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-[var(--color-text-muted)]">
                    {note.author_name || 'Staff'} · {note.shift} shift
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {new Date(note.created_at).toLocaleDateString('en-CA')}
                  </span>
                </div>
                <p className="text-sm text-[var(--color-text)]">{note.note}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}